import { WhenPopover } from "./WhenPopover";
import { useServices } from "@hq/client-react";
import type { Project, Task } from "@hq/contracts";
import type { NowContext } from "@hq/core";
import { Badge, Checkbox, cn, ContextMenu, IconButton, RenameInput } from "@z/ui";
import { CalendarDays, Clock, Flag, Folder, Moon, MoreHorizontal, Sunrise } from "lucide-react";
import { type MouseEvent, useRef, useState } from "react";
import { formatDueDate, formatDuration, formatScheduled, isOverdue } from "./format";
import {
  DueDatePopover,
  MoreMenu,
  TaskActionItems,
  PRIORITY_CLASS,
  PRIORITY_LABEL,
  PriorityMenu,
  ProjectMenu,
  SchedulePopover,
  TagsPopover,
} from "./rowActions";
import { useNow } from "./useNow";
import { TaskTitleButton } from "./TaskTitleButton";

export interface TaskRowProps {
  task: Task;
  /** Part of a multi-selection. */
  selected?: boolean;
  /** The roving-focus row of its list. */
  focused?: boolean;
  showProject?: boolean;
  density?: "compact" | "comfortable";
  onSelect?: (task: Task) => void;
  onOpen?: (task: Task) => void;
  /** Inline rename, controllable by the list (`e`). Uncontrolled when omitted. */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** The Deadline popover, controllable by the list (`d`). Uncontrolled when omitted. */
  dueDateOpen?: boolean;
  onDueDateOpenChange?: (open: boolean) => void;
  /** The Tags popover, controllable by the list (`t`). Uncontrolled when omitted. */
  tagsOpen?: boolean;
  onTagsOpenChange?: (open: boolean) => void;
  /** Makes the tag badges clickable, e.g. to filter the list by that tag. */
  onTagClick?: (tag: string) => void;
  /** Pins the clock; defaults to the shared ticking clock. */
  now?: NowContext;
  /** Position inside its list, for roving focus. */
  index?: number;
  /** Active projects, for the project chip and the move menu. Lists load them once (`useProjectIndex`). */
  projects: readonly Project[];
  className?: string;
}

/** Quiet hash-prefixed tags sit alongside the task metadata. */
const TAG_BADGE =
  "inline-flex h-4 items-center border-transparent bg-transparent p-0 text-xs font-normal text-fg-faint";

/**
 * One task in a list. Renders the task and dispatches narrow mutations; the row never holds
 * a copy of the task. Deadline and Scheduled are always labelled distinctly.
 */
export function TaskRow({
  task,
  selected = false,
  focused = false,
  showProject = true,
  density = "comfortable",
  onSelect,
  onOpen,
  editing: editingProp,
  onEditingChange,
  dueDateOpen,
  onDueDateOpenChange,
  tagsOpen,
  onTagsOpenChange,
  onTagClick,
  now: nowOverride,
  index,
  projects,
  className,
}: TaskRowProps) {
  const services = useServices();
  const menuHandoff = useRef(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const now = useNow(nowOverride);
  const [localEditing, setLocalEditing] = useState(false);
  const editing = editingProp ?? localEditing;
  const setEditing = (next: boolean) => {
    setLocalEditing(next);
    onEditingChange?.(next);
  };
  const moreRef = useRef<HTMLButtonElement>(null);
  const [action, setAction] = useState<"schedule" | "priority" | "project" | null>(null);
  const [localTagsOpen, setLocalTagsOpen] = useState(false);
  const tagsPopoverOpen = tagsOpen ?? localTagsOpen;
  const setTagsPopoverOpen = (next: boolean) => {
    setLocalTagsOpen(next);
    onTagsOpenChange?.(next);
  };

  const completed = task.status === "completed";
  const overdue = task.dueDate !== undefined && !completed && isOverdue(task.dueDate, now);
  const projectName =
    task.projectId !== undefined ? projects.find((p) => p.id === task.projectId)?.name : undefined;

  const onRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.detail === 2) {
      event.preventDefault();
      setEditing(true);
      return;
    }
    onSelect?.(task);
  };

  return (
    <ContextMenu.Root
      disabled={editing}
      onOpenChange={(open) => {
        if (open) menuHandoff.current = false;
      }}
    >
      <ContextMenu.Trigger
        render={
          <div
            ref={rowRef}
            role="option"
            data-slot="task-row"
            data-task-row=""
            data-task-id={task.id}
            data-task-index={index}
            data-density={density}
            data-focused={focused ? "" : undefined}
            data-selected={selected ? "" : undefined}
            data-completed={completed ? "" : undefined}
            tabIndex={focused ? 0 : -1}
            aria-selected={selected}
            onClick={onRowClick}
            className={cn(
              "group relative flex items-start gap-5 border-b border-border/80 px-2 outline-none transition-colors duration-fast",
              density === "compact" ? "min-h-9 py-1" : "min-h-[60px] py-2.5",
              "hover:bg-fg/4 data-selected:bg-selection data-focused:focus-visible:bg-fg/6 data-focused:ring-1 data-focused:ring-inset data-focused:ring-focus/40",
              completed && "opacity-60",
              className,
            )}
          >
            <Checkbox
              variant="task"
              aria-label={completed ? `Reopen “${task.title}”` : `Complete “${task.title}”`}
              checked={completed}
              onCheckedChange={() => void services.tasks.toggleComplete(task.id)}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "mt-[5px] size-[15px]",
                !completed && task.priority === "high" && "border-danger",
                !completed && task.priority === "medium" && "border-cal-orange-fg",
                !completed && task.priority === "low" && "border-cal-yellow-fg",
              )}
            />

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              {editing ? (
                <RenameInput
                  initial={task.title}
                  label="Task title"
                  className="h-7 px-1.5"
                  onSave={(title) => services.tasks.rename(task.id, title)}
                  onClose={() => setEditing(false)}
                />
              ) : (
                <TaskTitleButton
                  tabIndex={-1}
                  data-slot="task-title"
                  onOpen={() => {
                    if (onOpen) onOpen(task);
                    else onSelect?.(task);
                  }}
                  onRename={() => setEditing(true)}
                  className={cn(
                    "min-h-6 min-w-0 truncate text-left text-base text-fg outline-none",
                    completed && "line-through text-fg-muted",
                  )}
                >
                  {task.title}
                </TaskTitleButton>
              )}

              <div
                data-slot="task-meta"
                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-fg-faint empty:hidden"
              >
                {task.plannedDate && (
                  <WhenPopover
                    task={task}
                    now={now}
                    trigger={
                      <button
                        title="When"
                        className="inline-flex items-center gap-1 text-xs text-fg-muted"
                      >
                        <Clock className="size-3" />
                        {formatDueDate(task.plannedDate, now)}
                      </button>
                    }
                  />
                )}
                {task.scheduledAt !== undefined && (
                  <span
                    data-slot="task-scheduled"
                    title="Scheduled"
                    className="inline-flex items-center gap-1"
                  >
                    <Clock className="size-3" />
                    {formatScheduled(task.scheduledAt, now)}
                    {task.scheduledDurationMinutes !== undefined && (
                      <span className="text-fg-faint">
                        · {formatDuration(task.scheduledDurationMinutes)}
                      </span>
                    )}
                  </span>
                )}
                {task.priority !== undefined && (
                  <span
                    data-slot="task-priority"
                    title={`${PRIORITY_LABEL[task.priority]} priority`}
                    className={cn("sr-only", PRIORITY_CLASS[task.priority])}
                  >
                    <Flag className="size-3" />
                    {PRIORITY_LABEL[task.priority]}
                  </span>
                )}
                {showProject && projectName !== undefined && (
                  <span data-slot="task-project" className="inline-flex items-center gap-1">
                    <Folder className="size-3" />
                    {projectName}
                  </span>
                )}
                {task.evening && (
                  <span
                    data-slot="task-evening"
                    title="This evening"
                    className="inline-flex items-center"
                  >
                    <Moon className="size-3" />
                  </span>
                )}
                {task.status === "someday" && (
                  <Badge variant="outline" data-slot="task-someday" className="h-4 px-1">
                    <Sunrise />
                    Someday
                  </Badge>
                )}
                {task.tags?.map((tag) =>
                  onTagClick ? (
                    <button
                      key={tag}
                      type="button"
                      tabIndex={-1}
                      data-slot="task-tag"
                      title={`Filter by ${tag}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onTagClick(tag);
                      }}
                      className={cn(TAG_BADGE, "outline-none hover:bg-fg/8 hover:text-fg")}
                    >
                      #{tag}
                    </button>
                  ) : (
                    <Badge key={tag} data-slot="task-tag" className={TAG_BADGE}>
                      #{tag}
                    </Badge>
                  ),
                )}
              </div>
            </div>

            {task.dueDate !== undefined && (
              <span
                data-slot="task-deadline"
                title={`Deadline ${task.dueDate}`}
                className={cn(
                  "mt-1.5 inline-flex shrink-0 items-center gap-1 text-xs text-accent/70",
                  overdue && "text-danger",
                )}
              >
                <CalendarDays className="size-3" />
                {formatDueDate(task.dueDate, now)}
              </span>
            )}

            <div
              data-slot="task-actions"
              onClick={(e) => e.stopPropagation()}
              className="relative flex shrink-0 items-center gap-0.5 opacity-0 pointer-coarse:opacity-100 transition-opacity duration-fast group-hover:opacity-100 group-focus-within:opacity-100 group-data-focused:opacity-100 has-[[data-popup-open]]:opacity-100"
            >
              <DueDatePopover
                task={task}
                now={now}
                {...(dueDateOpen !== undefined ? { open: dueDateOpen } : {})}
                {...(onDueDateOpenChange ? { onOpenChange: onDueDateOpenChange } : {})}
                trigger={
                  <IconButton label="Deadline" size="sm" tabIndex={-1}>
                    <CalendarDays />
                  </IconButton>
                }
              />
              <MoreMenu
                task={task}
                onRename={() => requestAnimationFrame(() => setEditing(true))}
                onSchedule={() => setAction("schedule")}
                onPriority={() => setAction("priority")}
                onMove={() => setAction("project")}
                onEditTags={() => setTagsPopoverOpen(true)}
                keepFocus={editing || action !== null || tagsPopoverOpen}
                trigger={
                  <IconButton ref={moreRef} label="More" size="sm" tabIndex={-1}>
                    <MoreHorizontal />
                  </IconButton>
                }
              />
              <SchedulePopover
                task={task}
                now={now}
                anchorOnly
                returnFocus={moreRef}
                open={action === "schedule"}
                onOpenChange={(open) => setAction(open ? "schedule" : null)}
                trigger={
                  <span
                    aria-hidden="true"
                    tabIndex={-1}
                    className="pointer-events-none absolute inset-0"
                  />
                }
              />
              <PriorityMenu
                task={task}
                anchorOnly
                returnFocus={moreRef}
                open={action === "priority"}
                onOpenChange={(open) => setAction(open ? "priority" : null)}
                trigger={
                  <span
                    aria-hidden="true"
                    tabIndex={-1}
                    className="pointer-events-none absolute inset-0"
                  />
                }
              />
              <ProjectMenu
                task={task}
                projects={projects}
                anchorOnly
                returnFocus={moreRef}
                open={action === "project"}
                onOpenChange={(open) => setAction(open ? "project" : null)}
                trigger={
                  <span
                    aria-hidden="true"
                    tabIndex={-1}
                    className="pointer-events-none absolute inset-0"
                  />
                }
              />
              <TagsPopover
                task={task}
                anchorOnly
                returnFocus={moreRef}
                open={tagsPopoverOpen}
                onOpenChange={setTagsPopoverOpen}
                trigger={
                  <span
                    aria-hidden="true"
                    tabIndex={-1}
                    className="pointer-events-none absolute inset-0"
                  />
                }
              />
            </div>
          </div>
        }
      />
      <ContextMenu.Content
        aria-label={`Task actions for ${task.title}`}
        finalFocus={() => (menuHandoff.current ? false : (rowRef.current ?? false))}
      >
        <TaskActionItems
          task={task}
          onRename={() => {
            menuHandoff.current = true;
            // Mount the editor after the menu has released its focus scope.
            requestAnimationFrame(() => setEditing(true));
          }}
          onSchedule={() => {
            menuHandoff.current = true;
            setAction("schedule");
          }}
          onPriority={() => {
            menuHandoff.current = true;
            setAction("priority");
          }}
          onMove={() => {
            menuHandoff.current = true;
            setAction("project");
          }}
          onEditTags={() => {
            menuHandoff.current = true;
            setTagsPopoverOpen(true);
          }}
        />
      </ContextMenu.Content>
    </ContextMenu.Root>
  );
}
